import { motion } from "framer-motion";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import CountUp from "./CountUp";
import { PRODUCTS, WA_LINK } from "../lib/data";
import { useLang } from "../lib/i18n";

const EASE = [0.16, 1, 0.3, 1];

function CertFrame({ product, viewReportLabel, name }) {
  return (
    <a
      href={product.reportUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`report-link-${product.key}`}
      aria-label={`${viewReportLabel} — ${name}`}
    >
      <div className="group relative aspect-[4/3] overflow-hidden rounded-3xl border border-cream/10 bg-espresso">
        <img
          src={product.reportImg}
          alt={`${name} lab test certificate`}
          className="h-full w-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-110"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-ink/70 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
          <span className="inline-flex items-center gap-2 rounded-full bg-cream px-5 py-2.5 font-display text-sm font-bold text-ink">
            {viewReportLabel} <ArrowUpRight size={16} strokeWidth={2.5} className="rtl:-scale-x-100" />
          </span>
        </div>
      </div>
    </a>
  );
}

export default function Products() {
  const { t } = useLang();
  return (
    <div data-testid="products-cards">
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE }}
      >
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
          {t.productsPre} <em className="font-serif italic font-medium text-brasslight">{t.productsAccent}</em>
        </h2>
        <p className="mt-4 text-base md:text-lg text-taupe">{t.productsSub}</p>
      </motion.div>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {PRODUCTS.map((p, i) => {
          const c = t.productItems[i];
          return (
            <motion.article
              key={p.key}
              initial={{ opacity: 0, y: 44 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-70px" }}
              transition={{ duration: 0.85, delay: i * 0.14, ease: EASE }}
              className="rounded-[2rem] bg-espresso border border-cream/5 p-6 sm:p-8 shadow-[0_24px_70px_rgba(0,0,0,0.3)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_44px_100px_rgba(0,0,0,0.5)]"
              data-testid={`product-card-${p.key}`}
            >
              <CertFrame product={p} viewReportLabel={t.viewReport} name={c.name} />
              <p className="mt-7 text-xs font-bold uppercase text-taupe">{c.label}</p>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{c.name}</h3>
                {c.tags.map((tag, ti) => (
                  <span
                    key={tag}
                    className={`rounded-full px-3 py-1.5 text-[0.7rem] font-bold ${
                      ti === 1
                        ? "shine bg-brass/15 text-brasslight border border-brass/30"
                        : "bg-cream/5 text-cream/70 border border-cream/10"
                    }`}
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <p className="mt-4 text-sm sm:text-base leading-relaxed text-cream/70">{c.desc}</p>

              <div className="mt-7 grid grid-cols-3 gap-3">
                {p.stats.map((s, si) => (
                  <div key={si} className="rounded-2xl bg-ink/60 border border-cream/5 px-4 py-4 text-center">
                    <div className="font-display text-xl sm:text-2xl font-extrabold text-brasslight">
                      <CountUp to={s.value} decimals={s.decimals} />
                    </div>
                    <div className="mt-0.5 text-[0.65rem] font-bold text-brass/80">{s.unit}</div>
                    <div className="mt-1.5 text-[0.65rem] font-semibold text-taupe leading-tight">
                      {c.statLabels[si]}
                    </div>
                  </div>
                ))}
              </div>

              <p className="mt-5 text-xs font-semibold text-taupe">{c.note}</p>

              <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`order-whatsapp-${p.key}-button`}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brass px-6 py-3.5 font-display text-sm font-bold text-ink transition-all duration-300 hover:bg-brasslight active:scale-[0.98]"
              >
                <MessageCircle size={17} strokeWidth={2.4} />
                {t.heroCta}
              </a>
            </motion.article>
          );
        })}
      </div>

      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="mt-8 text-center text-sm font-semibold text-taupe"
      >
        {t.productsFootnote}
      </motion.p>
    </div>
  );
}
